/**
 * Pure tour-engine helpers (no React). Unit-tested in
 * __tests__/tourTests/tourEngine.test.js.
 */

/**
 * Does `pathname` match a route pattern? Supports `:param` segments and a
 * trailing `*`. Trailing slashes are ignored. Exact otherwise.
 *   matchRoute("/bdr-office/clients/view-client/:id", "/bdr-office/clients/view-client/abc") → true
 */
export function matchRoute(pattern, pathname) {
  if (!pattern || typeof pathname !== "string") return false;
  const norm = (s) => s.replace(/\/+$/, "") || "/";
  const pat = norm(pattern).split("/");
  const path = norm(pathname).split("/");
  for (let i = 0; i < pat.length; i += 1) {
    if (pat[i] === "*") return true;
    if (i >= path.length) return false;
    if (pat[i].startsWith(":")) {
      if (!path[i]) return false;
      continue;
    }
    if (pat[i] !== path[i]) return false;
  }
  return pat.length === path.length;
}

/** Every route a tour legitimately visits: its start route plus step routes. */
export function tourRoutes(tourDef) {
  if (!tourDef) return [];
  const routes = [tourDef.route, ...(tourDef.steps || []).map((s) => s && s.route)];
  return [...new Set(routes.filter(Boolean))];
}

/** Is `pathname` somewhere this tour is allowed to be? (else: user navigated away) */
export function isOnTourRoute(tourDef, pathname) {
  const routes = tourRoutes(tourDef);
  // A tour with no routes at all is page-agnostic.
  if (routes.length === 0) return true;
  return routes.some((r) => matchRoute(r, pathname));
}

/**
 * The route a step must be on before it shows. Step 0 falls back to the tour's
 * start route (unless the tour is "here-only", which never navigates to start);
 * later steps without a route stay wherever the tour already is.
 * `startMode` comes from the server catalog item, not the step registry.
 */
export function routeForStep(tourDef, index, startMode = "navigate") {
  const step = tourDef && tourDef.steps && tourDef.steps[index];
  if (!step) return null;
  if (step.route) return step.route;
  if (index === 0 && startMode !== "here-only") return tourDef.route || null;
  return null;
}

/** A route the engine can navigate to by itself (no :params, no wildcard). */
export const isConcreteRoute = (route) =>
  typeof route === "string" && !route.includes(":") && !route.includes("*");

/**
 * Server items this client can actually run: platform already web (the query
 * asks for it), and a step definition exists here. Everything else is dropped.
 */
export function knownItems(items, registry) {
  return (items || []).filter((it) => it && registry && registry[it.id]);
}

/** Ids the server knows that this build doesn't (for one-time unknown_tour logging). */
export function unknownIds(items, registry) {
  return (items || []).filter((it) => it && !(registry && registry[it.id])).map((it) => it.id);
}

/** Items for the What's New list: new and not yet seen. */
export const newItems = (items) => (items || []).filter((it) => it.isNew);

/**
 * An admin assignment is a to-do: the server keeps it `isNew` until it is
 * COMPLETED, whatever the user does with it. So a start or a dismiss must not
 * clear `isNew` locally for it (the next load would only put it back), and the
 * hub offers no Dismiss for it.
 */
export const keepsNew = (it) => !!(it && it.assigned && !it.completedAt);

/** Items the one-time popup should show: new AND never included in a popup. */
export const popupItems = (items) => newItems(items).filter((it) => !it.poppedAt);

/** Help "This page": tours whose start route (or any step route) matches here. */
export function itemsForPage(items, registry, pathname) {
  return (items || []).filter((it) => {
    const def = registry && registry[it.id];
    return def && tourRoutes(def).some((r) => matchRoute(r, pathname));
  });
}

/**
 * Can this tour be started from `pathname`? "navigate" tours start anywhere
 * (the engine navigates). "here-only" tours need a record id in the URL, so
 * they start only from their own page.
 */
export function canStartHere(item, registry, pathname) {
  const def = registry && registry[item.id];
  if (!def || !def.steps || def.steps.length === 0) return false;
  if (item.startMode === "here-only") return matchRoute(def.route, pathname);
  return true;
}

/**
 * First VISIBLE element carrying data-tour="<anchor>" (duplicates allowed).
 * `last: true` takes the last visible one instead — for a step shown inside a
 * modal: modals portal to the END of <body>, after #root, so an id that also
 * exists on the page behind would otherwise resolve to the covered copy.
 */
export function findAnchor(anchor, root = typeof document !== "undefined" ? document : null, { last = false } = {}) {
  if (!anchor || !root) return null;
  const esc =
    typeof CSS !== "undefined" && CSS.escape ? CSS.escape(anchor) : String(anchor).replace(/"/g, '\\"');
  const visible = Array.from(root.querySelectorAll(`[data-tour="${esc}"]`)).filter(
    (el) => el.getClientRects().length > 0
  );
  if (!visible.length) return null;
  return last ? visible[visible.length - 1] : visible[0];
}

/** The modal tags a step is shown inside (`modal`: a tag or an array). */
export function stepModals(step) {
  const m = step && step.modal;
  if (!m) return [];
  return (Array.isArray(m) ? m : [m]).filter((t) => typeof t === "string" && t.length > 0);
}

/**
 * Resolve when `check()` returns truthy, polling every animation frame (plus a
 * MutationObserver nudge), or resolve null after `timeoutMs` — or as soon as
 * `shouldStop()` turns true (the tour ended), so nothing keeps polling.
 */
export function waitFor(check, timeoutMs = 4000, shouldStop = () => false) {
  return new Promise((resolve) => {
    const first = check();
    if (first) {
      resolve(first);
      return;
    }
    let done = false;
    let raf = 0;
    let mo = null;
    const finish = (value) => {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      if (mo) mo.disconnect();
      resolve(value);
    };
    const tick = () => {
      if (shouldStop()) {
        finish(null);
        return;
      }
      const v = check();
      if (v) finish(v);
      else raf = requestAnimationFrame(tick);
    };
    // One last check before giving up: in a background tab animation frames
    // stop, and the registry-based checks (modal open / closed) change AFTER
    // the DOM mutation the observer reacts to, so neither path may have seen
    // a condition that is already true.
    // Guarded: the timer is the backstop that must ALWAYS settle the promise,
    // so a throwing check here resolves null rather than leaving it pending.
    const timer = setTimeout(() => {
      let v = null;
      try {
        v = shouldStop() ? null : check() || null;
      } catch (_) {
        v = null;
      }
      finish(v);
    }, timeoutMs);
    if (typeof MutationObserver !== "undefined" && typeof document !== "undefined") {
      mo = new MutationObserver(() => {
        const v = check();
        if (v) finish(v);
      });
      mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    }
    raf = requestAnimationFrame(tick);
  });
}

/**
 * Where the card goes relative to the spotlight rect. Returns fixed-position
 * { top, left, placement }. Narrow screens (≤600px) dock the card to the
 * bottom edge instead (handled in CSS via `docked`).
 */
export function placeCard(rect, cardSize, viewport, preferred = "auto", gap = 12) {
  const { width: vw, height: vh } = viewport;
  const { width: cw, height: ch } = cardSize;
  if (vw <= 600) return { docked: true };
  if (!rect) {
    return { top: Math.max(gap, (vh - ch) / 2), left: Math.max(gap, (vw - cw) / 2), placement: "center" };
  }
  const below = rect.bottom + gap;
  const above = rect.top - gap - ch;
  const fitsBelow = below + ch <= vh - gap;
  const fitsAbove = above >= gap;
  let placement = preferred;
  if (placement === "auto" || (placement === "bottom" && !fitsBelow) || (placement === "top" && !fitsAbove)) {
    placement = fitsBelow || !fitsAbove ? "bottom" : "top";
  }
  const top = placement === "bottom" ? Math.min(below, vh - gap - ch) : Math.max(gap, above);
  const centeredLeft = rect.left + rect.width / 2 - cw / 2;
  const left = Math.min(Math.max(gap, centeredLeft), Math.max(gap, vw - gap - cw));
  return { top, left, placement };
}
